import { DialogComponent } from '@theme/dialog';

/**
 * The size guide dialog: shows the chosen size to scale on the wall of a room photo, with its width and
 * height marked: a single piece above a 4 ft console, a set's panels side by side above a 6 ft sofa, a set
 * of 6 as a 3 x 2 grid above a 6 ft bench.
 *
 * @typedef {object} SizeGuideRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} scene
 * @property {HTMLElement} dims
 * @property {HTMLElement[]} panelWidths - The width labels, one beneath each panel.
 * @property {HTMLElement} heightLabel
 * @property {HTMLImageElement} [room] - The room photo; its `data-src` is loaded on first open.
 * @property {HTMLImageElement[]} [samples] - The sample artwork, one per panel; `data-src` is loaded on first open.
 * @property {HTMLButtonElement[]} sizes - One per size, with `data-width` and `data-height` in inches.
 *
 * @extends {DialogComponent}
 */
class SizeGuideComponent extends DialogComponent {
  requiredRefs = ['dialog', 'scene', 'dims', 'panelWidths', 'heightLabel', 'sizes'];

  /**
   * Opens on the size currently selected on the product. Set up on every open, because the variant
   * picker morphs this markup back to its server-rendered state whenever an option changes.
   */
  showDialog() {
    const { sizes, room, samples = [] } = /** @type {SizeGuideRefs} */ (/** @type {unknown} */ (this.refs));
    for (const image of [room, ...samples]) {
      if (image && !image.getAttribute('src') && image.dataset.src) image.src = image.dataset.src;
    }
    const selected = sizes.findIndex((button) => button.getAttribute('aria-pressed') === 'true');
    this.selectSize(Math.max(selected, 0));

    super.showDialog();
  }

  /**
   * Shows one size on the wall.
   * @param {number} index - Position of the size among the size buttons.
   */
  selectSize(index) {
    const { sizes, scene, dims, panelWidths, heightLabel } = /** @type {SizeGuideRefs} */ (
      /** @type {unknown} */ (this.refs)
    );
    const button = sizes[index];
    if (!button) return;

    const width = Number(button.dataset.width);
    const height = Number(button.dataset.height);
    if (!width || !height) return;

    scene.style.setProperty('--w', String(width));
    scene.style.setProperty('--h', String(height));
    sizes.forEach((size) => size.setAttribute('aria-pressed', String(size === button)));

    for (const label of panelWidths) label.textContent = `${width} in`;
    heightLabel.textContent = `${height} in`;

    const cm = (/** @type {number} */ inches) => Math.round(inches * 2.54);
    const size = `${button.textContent?.trim()} in · ${cm(width)}x${cm(height)} cm`;
    const panels = Number(scene.dataset.panels) || 1;
    dims.textContent = panels > 1 ? `${panels} × ${size} each` : size;
  }
}

if (!customElements.get('size-guide-component')) {
  customElements.define('size-guide-component', SizeGuideComponent);
}
